import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

const ZoomOnly = ({
  value = 1, // controlled zoom
  onChange, // (next:number) => void
  min = 1, // never below 100%
  max = 8,
  className = "",
  style = {},
  children,
}) => {
  const scrollerRef = useRef(null); // overflow:auto
  const layerRef = useRef(null); // absolute layer holding scaled content
  const contentRef = useRef(null); // element to scale
  const [base, setBase] = useState({ w: 1, h: 1 }); // natural content size
  const scaleRef = useRef(value);

  // Measure natural size (transforms don't affect offsetWidth/Height)
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () =>
      setBase({ w: el.offsetWidth || 1, h: el.offsetHeight || 1 });
    measure();

    // Track size changes (image load, canvas resize, etc.)
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Apply scale to content
  useEffect(() => {
    scaleRef.current = value;
    const el = contentRef.current;
    if (!el) return;
    el.style.transformOrigin = "0 0";
    el.style.transform = `scale(${value})`;
    el.style.willChange = "transform";
  }, [value]);

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onWheelCapture = (e) => {
      // If NOT pinch/Ctrl+wheel, perform native-like scrolling here in capture phase
      // so the canvas can't swallow the event.
      if (!e.ctrlKey) {
        // Normalize delta to pixels
        const rect = scroller.getBoundingClientRect();
        const unit =
          e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;

        // Scroll the container (both axes supported)
        scroller.scrollLeft += e.deltaX * unit;
        scroller.scrollTop += e.deltaY * unit;

        // Stop canvas handlers from canceling the scroll
        e.preventDefault();
        return;
      }

      // --- Pinch / Ctrl+wheel → ZOOM ---
      const prev = scaleRef.current;

      // Mouse in scroller coords
      const rect = scroller.getBoundingClientRect();
      const mx = e.clientX - rect.left - scroller.clientLeft;
      const my = e.clientY - rect.top - scroller.clientTop;

      // Convert to content coords (pre-zoom)
      const cx = (scroller.scrollLeft + mx) / prev;
      const cy = (scroller.scrollTop + my) / prev;

      // Smooth, device-independent zoom
      const factor = Math.exp(-e.deltaY * 0.0035); // deltaY>0 out, <0 in
      let next = clamp(prev * factor, min, max);
      if (Math.abs(next - min) < 1e-3) next = min; // snap near min

      // Apply (controlled/uncontrolled)
      scaleRef.current = next;
      if (onChange) onChange(next);
      else if (contentRef.current) {
        contentRef.current.style.transform = `scale(${next})`;
      }

      // Recenter scroll so (cx,cy) stays under mouse
      const targetLeft = cx * next - mx;
      const targetTop = cy * next - my;

      // Clamp to new scrollable range based on spacer size
      const maxLeft = base.w * next - scroller.clientWidth;
      const maxTop = base.h * next - scroller.clientHeight;
      scroller.scrollLeft = clamp(targetLeft, 0, Math.max(0, maxLeft));
      scroller.scrollTop = clamp(targetTop, 0, Math.max(0, maxTop));

      // Prevent browser/page zoom and other handlers
      e.preventDefault();
    };

    scroller.addEventListener("wheel", onWheelCapture, {
      passive: false,
      capture: true,
    });
    return () =>
      scroller.removeEventListener("wheel", onWheelCapture, { capture: true });
  }, [min, max, onChange, base.w, base.h]);

  // Spacer grows with zoom to create real scrollbars
  const scaledW = Math.max(1, Math.round(base.w * value));
  const scaledH = Math.max(1, Math.round(base.h * value));

  return (
    <div
      ref={scrollerRef}
      className={className}
      style={{
        position: "relative",
        overflow: "auto",
        touchAction: "none",
        ...style,
      }}
    >
      {/* Spacer defines the scrollable area */}
      <div style={{ width: scaledW, height: scaledH }} />

      {/* Scaled content sits absolutely at (0,0), independent of layout size */}
      <div
        ref={layerRef}
        style={{ position: "absolute", left: 0, top: 0, width: "100%" }}
      >
        <div
          ref={contentRef}
          style={{
            transformOrigin: "0 0",
            display: "inline-block",
            width: "100%",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
export default ZoomOnly;
